// Firebase Storage Service
import { 
  ref, 
  uploadBytes, 
  uploadBytesResumable,
  getDownloadURL, 
  deleteObject,
  listAll 
} from 'firebase/storage';
import { storage } from '../firebase';
import logger from '../utils/logger';

/**
 * Tek bir dosya yükle
 * @param {File} file - Yüklenecek dosya
 * @param {string} folder - Klasör adı (örn: 'models', 'users', 'products')
 * @param {Function} onProgress - İlerleme callback'i (opsiyonel)
 * @returns {Promise<Object>} - { success, url, error }
 */
export const uploadFile = async (file, folder = 'uploads', onProgress = null) => {
  try {
    logger.log('Starting upload process...');
    logger.log('File details:', {
      name: file.name,
      size: file.size,
      type: file.type,
      folder: folder
    });

    // Dosya adını temizle ve benzersiz yap
    const timestamp = Date.now();
    const cleanFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const fileName = `${timestamp}_${cleanFileName}`;
    const fullPath = `${folder}/${fileName}`;
    
    logger.log('Storage path:', fullPath);
    
    const storageRef = ref(storage, fullPath);
    
    // Progress tracking istiyorsa uploadBytesResumable kullan
    if (onProgress) {
      return new Promise((resolve, reject) => {
        logger.log('Using uploadBytesResumable...');
        const uploadTask = uploadBytesResumable(storageRef, file);
        
        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            logger.log(`Upload progress: ${progress}%`);
            onProgress(progress);
          },
          (error) => {
            logger.error('Upload error:', error.code, error.message);
            const msg = error.code === 'storage/unknown' ? 'Görsel yüklenemedi.' : error.message;
            reject({ success: false, error: msg, code: error.code });
          },
          async () => {
            try {
              logger.log('Upload completed, getting download URL...');
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              logger.log('Download URL obtained:', downloadURL);
              resolve({ success: true, url: downloadURL, fileName });
            } catch (urlError) {
              logger.error('Error getting download URL:', urlError);
              reject({ success: false, error: urlError.message });
            }
          }
        );
      });
    }
    
    // Progress tracking yoksa hızlı yükleme
    logger.log('Using uploadBytes...');
    const snapshot = await uploadBytes(storageRef, file);
    logger.log('Upload snapshot:', snapshot);
    
    const downloadURL = await getDownloadURL(snapshot.ref);
    logger.log('Download URL:', downloadURL);
    
    return { success: true, url: downloadURL, fileName };
  } catch (error) {
    logger.error('Upload file error:', error?.code, error?.message, error);
    const msg = getStorageErrorMessage(error);
    return { success: false, error: msg, code: error?.code };
  }
};

/**
 * Firebase Storage hata kodunu kullanıcıya gösterilebilir Türkçe metne çevirir
 * @param {Error} error
 * @returns {string}
 */
function getStorageErrorMessage(error) {
  if (!error) return 'Görsel yüklenemedi.';
  const code = error.code || '';
  const fallback = error.message && String(error.message).trim() ? String(error.message) : null;
  const messages = {
    'storage/unauthorized': 'Yetkiniz yok. Bu klasöre yükleme yapılamıyor.',
    'storage/canceled': 'Yükleme iptal edildi.',
    'storage/unknown': 'Görsel yüklenemedi. Bağlantı veya dosya hatası.',
    'storage/object-not-found': 'Dosya bulunamadı.',
    'storage/quota-exceeded': 'Depolama kotası aşıldı.',
    'storage/unauthenticated': 'Giriş yapmanız gerekiyor.',
    'storage/retry-limit-exceeded': 'Sunucu yanıt vermedi. Lütfen tekrar deneyin.'
  };
  return messages[code] || fallback || 'Görsel yüklenemedi. Lütfen tekrar deneyin.';
}

/**
 * Birden fazla dosya yükle
 * @param {FileList|Array} files - Dosya listesi
 * @param {string} folder - Klasör adı
 * @param {Function} onProgress - Toplam ilerleme callback'i
 * @returns {Promise<Object>} - { success, urls, errors }
 */
export const uploadMultipleFiles = async (files, folder = 'uploads', onProgress = null) => {
  try {
    const fileArray = Array.from(files);
    const totalFiles = fileArray.length;
    let completedFiles = 0;
    
    const uploadPromises = fileArray.map(async (file) => {
      const result = await uploadFile(file, folder, (progress) => {
        if (onProgress) {
          const totalProgress = ((completedFiles + (progress / 100)) / totalFiles) * 100;
          onProgress(totalProgress);
        }
      });
      
      if (result.success) {
        completedFiles++;
      }
      
      return result;
    });
    
    const results = await Promise.all(uploadPromises);
    
    const successfulUploads = results.filter(r => r.success);
    const failedUploads = results.filter(r => !r.success);
    
    return {
      success: successfulUploads.length > 0,
      urls: successfulUploads.map(r => r.url),
      fileNames: successfulUploads.map(r => r.fileName),
      errors: failedUploads.map(r => r.error),
      totalUploaded: successfulUploads.length,
      totalFailed: failedUploads.length
    };
  } catch (error) {
    logger.error('Upload multiple files error:', error);
    return { success: false, error: error.message, urls: [] };
  }
};

/**
 * Dosya sil
 * @param {string} fileUrl - Silinecek dosya URL'i
 * @returns {Promise<Object>} - { success, error }
 */
export const deleteFile = async (fileUrl) => {
  try {
    // URL'den storage path'ini çıkar
    const fileRef = ref(storage, fileUrl);
    await deleteObject(fileRef);
    
    return { success: true };
  } catch (error) {
    logger.error('Delete file error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Birden fazla dosya sil
 * @param {Array<string>} fileUrls - Silinecek dosya URL'leri
 * @returns {Promise<Object>} - { success, totalDeleted, errors }
 */
export const deleteMultipleFiles = async (fileUrls) => {
  try {
    const deletePromises = fileUrls.map(url => deleteFile(url));
    const results = await Promise.all(deletePromises);
    
    const successful = results.filter(r => r.success).length;
    const failed = results.filter(r => !r.success);
    
    return {
      success: successful > 0,
      totalDeleted: successful,
      totalFailed: failed.length,
      errors: failed.map(r => r.error)
    };
  } catch (error) {
    logger.error('Delete multiple files error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Klasördeki dosyaları listele
 * @param {string} folder - Klasör adı
 * @returns {Promise<Object>} - { success, files, error }
 */
export const listFiles = async (folder) => {
  try {
    const folderRef = ref(storage, folder);
    const result = await listAll(folderRef);
    
    const filePromises = result.items.map(async (itemRef) => {
      const url = await getDownloadURL(itemRef);
      return {
        name: itemRef.name,
        fullPath: itemRef.fullPath,
        url: url
      };
    });
    
    const files = await Promise.all(filePromises);
    
    return { success: true, files };
  } catch (error) {
    logger.error('List files error:', error);
    return { success: false, error: error.message, files: [] };
  }
};

/**
 * Dosya boyutu kontrolü
 * @param {File} file - Kontrol edilecek dosya
 * @param {number} maxSizeMB - Maksimum boyut (MB)
 * @returns {boolean}
 */
export const validateFileSize = (file, maxSizeMB = 10) => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};

/**
 * Dosya tipi kontrolü
 * @param {File} file - Kontrol edilecek dosya
 * @param {Array<string>} allowedTypes - İzin verilen tipler
 * @returns {boolean}
 */
export const validateFileType = (file, allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp']) => {
  return allowedTypes.includes(file.type);
};

/**
 * Resmi boyutlandır (client-side)
 * @param {File} file - Boyutlandırılacak resim dosyası
 * @param {number} maxWidth - Maksimum genişlik
 * @param {number} maxHeight - Maksimum yükseklik
 * @returns {Promise<Blob>}
 */
export const resizeImage = async (file, maxWidth = 1200, maxHeight = 1200) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // Oranı koru
        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob((blob) => {
          resolve(blob);
        }, file.type, 0.9);
      };
      
      img.onerror = reject;
    };
    
    reader.onerror = reject;
  });
};

/**
 * Thumbnail oluştur
 * @param {File} file - Kaynak dosya
 * @param {number} size - Thumbnail boyutu (px)
 * @returns {Promise<Blob>}
 */
export const createThumbnail = async (file, size = 200) => {
  return resizeImage(file, size, size);
};

/**
 * react-easy-crop'ın croppedAreaPixels çıktısıyla görseli kırpar
 * @param {string} imageSrc - Görsel URL veya Object URL
 * @param {Object} cropPixels - { x, y, width, height } piksel cinsinden kırpma alanı
 * @returns {Promise<Blob>} Kırpılmış görsel Blob'u
 */
export const getCroppedImage = (imageSrc, cropPixels) => {
  const cropWithImage = (imgEl) => {
    const canvas = document.createElement('canvas');
    canvas.width = cropPixels.width;
    canvas.height = cropPixels.height;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      imgEl,
      cropPixels.x, cropPixels.y,
      cropPixels.width, cropPixels.height,
      0, 0,
      cropPixels.width, cropPixels.height
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas toBlob failed'));
      }, 'image/jpeg', 0.92);
    });
  };

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      cropWithImage(img).then(resolve).catch(() => {
        // Canvas tainted — retry without crossOrigin (Object URL'ler için)
        const img2 = new Image();
        img2.onload = () => cropWithImage(img2).then(resolve).catch(reject);
        img2.onerror = () => reject(new Error('Image load failed'));
        img2.src = imageSrc;
      });
    };

    img.onerror = () => {
      // crossOrigin ile yüklenemiyorsa onsuz dene
      const img2 = new Image();
      img2.onload = () => cropWithImage(img2).then(resolve).catch(reject);
      img2.onerror = () => reject(new Error('Image load failed'));
      img2.src = imageSrc;
    };

    img.src = imageSrc;
  });
};

/**
 * Firebase Storage URL'inden storage path'ini çıkarır ve yeni token ile URL döndürür
 * @param {string} url - Firebase Storage URL (expired token içerebilir)
 * @returns {Promise<string|null>} - Yeni token ile güncellenmiş URL veya null
 */
export const refreshStorageURL = async (url) => {
  try {
    // URL formatı: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{encodedPath}?alt=media&token={token}
    const match = url.match(/\/o\/([^?]+)/);
    if (!match) {
      logger.warn('Invalid Firebase Storage URL format:', url);
      return null;
    }
    
    const encodedPath = match[1];
    const storagePath = decodeURIComponent(encodedPath);
    const storageRef = ref(storage, storagePath);
    
    // Yeni download URL al (yeni token ile)
    const newUrl = await getDownloadURL(storageRef);
    return newUrl;
  } catch (error) {
    logger.error('Refresh storage URL error:', error);
    return null;
  }
};

